import { html, render, nothing, type TemplateResult, type SVGTemplateResult } from "lit-html";
import { api, qs, setUnauthorizedHandler, setNeed2faHandler, ApiError } from "./api.js";
import type { Me, DataStatus } from "./types.js";
import { icons } from "./icons.js";
import { initials, timeAgo, fmtDate } from "./format.js";
import { hideTip } from "./tooltip.js";

export type Page = "overview" | "divisions" | "reps" | "map" | "opportunities" | "explore" | "board" | "customer" | "account" | "users";

export interface Filters { year: number; manager: number | null; rep: number | null; division: string | null }

export interface View<D = unknown> {
  title: (app: App) => string;
  /** Which global filters this page shows. */
  filters: ("year" | "manager" | "rep" | "division")[];
  load: (app: App, param: string | undefined) => Promise<D>;
  render: (app: App, data: D, param: string | undefined) => TemplateResult;
}

const NAV: { page: Page; label: string; icon: SVGTemplateResult }[] = [
  { page: "overview", label: "Overview", icon: icons.overview },
  { page: "divisions", label: "Divisions", icon: icons.divisions },
  { page: "reps", label: "Reps", icon: icons.reps },
  { page: "map", label: "Coverage Map", icon: icons.map },
  { page: "opportunities", label: "Opportunities", icon: icons.target },
  { page: "explore", label: "Explore", icon: icons.explore },
  { page: "board", label: "My Board", icon: icons.board },
];

export class App {
  me!: Me;
  status!: DataStatus;
  filters: Filters = { year: new Date().getFullYear(), manager: null, rep: null, division: null };
  page: Page = "overview";
  param: string | undefined;
  private views: Record<Page, View<any>>;
  private data: unknown = null;
  private dataKey = "";
  private error: string | null = null;
  private loading = false;
  private reqId = 0;
  private toastTimer = 0;
  private pollTimer = 0;
  private root: HTMLElement;

  constructor(root: HTMLElement, views: Record<Page, View<any>>) {
    this.root = root;
    this.views = views;
    setUnauthorizedHandler(() => location.reload());
    setNeed2faHandler(() => { if (this.page !== "account") location.hash = "#/account"; });
  }

  async start(me: Me) {
    this.me = me;
    this.status = me.status;
    this.filters.year = Number(me.status.asOf.slice(0, 4));
    this.readHash();
    window.addEventListener("hashchange", () => { this.readHash(); void this.load(); });
    await this.load();
    this.schedulePoll();
    document.addEventListener("visibilitychange", () => { if (!document.hidden) void this.poll(); });
  }

  /* ---------------- routing ---------------- */

  private readHash() {
    const raw = location.hash.replace(/^#\/?/, "");
    const [path = "", query = ""] = raw.split("?");
    const [page, param] = path.split("/");
    this.page = (page && page in this.views ? page : "overview") as Page;
    if (this.me.twoFactor.required && !this.me.twoFactor.enabled) this.page = "account"; // must set up 2FA first
    this.param = param ? decodeURIComponent(param) : undefined;
    const p = new URLSearchParams(query);
    const curYear = Number(this.status.asOf.slice(0, 4));
    const y = Number(p.get("year"));
    this.filters = {
      year: Number.isInteger(y) && y >= 2018 && y <= curYear ? y : curYear,
      manager: p.get("manager") && this.me.managers.some((m) => String(m.id) === p.get("manager")) ? Number(p.get("manager")) : null,
      rep: p.get("rep") && this.me.reps.some((r) => String(r.id) === p.get("rep")) ? Number(p.get("rep")) : null,
      division: p.get("division") && this.me.divisions.some((d) => d.key === p.get("division")) ? p.get("division") : null,
    };
  }

  href(page: Page, param?: string, filters: Partial<Filters> = {}): string {
    const f = { ...this.filters, ...filters };
    const curYear = Number(this.status.asOf.slice(0, 4));
    const q = qs({ year: f.year !== curYear ? f.year : null, manager: f.manager, rep: f.rep, division: f.division });
    return `#/${page}${param ? "/" + encodeURIComponent(param) : ""}${q}`;
  }

  go(page: Page, param?: string, filters: Partial<Filters> = {}) {
    location.hash = this.href(page, param, filters).slice(1);
  }

  setFilter(patch: Partial<Filters>) {
    const next = { ...this.filters, ...patch };
    // A rep from another manager's team makes no sense once a manager is picked.
    if (patch.manager !== undefined && next.rep !== null) {
      const rep = this.me.reps.find((r) => r.id === next.rep);
      if (next.manager !== null && rep && rep.managerId !== next.manager) next.rep = null;
    }
    location.hash = this.href(this.page, this.param, next).slice(1);
  }

  /** Query string for the data endpoints from the current filters. */
  q(extra: Record<string, string | number | null | undefined> = {}): string {
    const f = this.filters;
    return qs({ year: f.year, manager: f.manager, rep: f.rep, division: f.division, ...extra });
  }

  /** Selected year as a date window: Jan 1 → data-through date (or Dec 31 for past years). */
  yearRange(): { from: string; to: string } {
    const y = this.filters.year;
    const asOf = this.status.asOf;
    return { from: `${y}-01-01`, to: Number(asOf.slice(0, 4)) > y ? `${y}-12-31` : asOf };
  }

  /** Global filters in the shape the query engine wants. */
  queryFilters(): { rep?: number[]; manager?: number[]; division?: string[] } {
    const f = this.filters;
    return {
      ...(f.rep !== null ? { rep: [f.rep] } : {}),
      ...(f.manager !== null ? { manager: [f.manager] } : {}),
      ...(f.division !== null ? { division: [f.division] } : {}),
    };
  }

  /* ---------------- data ---------------- */

  async load(silent = false) {
    const view = this.views[this.page];
    const key = `${this.page}|${this.param}|${JSON.stringify(this.filters)}`;
    const id = ++this.reqId;
    if (!silent) {
      if (key !== this.dataKey) this.data = null;
      this.loading = true;
      this.error = null;
      this.render();
    }
    try {
      const data = await view.load(this, this.param);
      if (id !== this.reqId) return;
      this.data = data;
      this.dataKey = key;
      this.error = null;
    } catch (e) {
      if (id !== this.reqId) return;
      if (!silent) this.error = e instanceof ApiError || e instanceof Error ? e.message : String(e);
    } finally {
      if (id === this.reqId) { this.loading = false; this.render(); }
    }
  }

  reload() { void this.load(true); }

  private schedulePoll() {
    window.clearTimeout(this.pollTimer);
    this.pollTimer = window.setTimeout(() => void this.poll(), Math.max(15, this.me.pollSeconds) * 1000);
  }

  private async poll() {
    try {
      const st = await api.status();
      // A new version of the dashboard was installed: load it.
      if (st.build && this.status.build && st.build !== this.status.build) { location.reload(); return; }
      const changed = st.version !== this.status.version;
      this.status = st;
      if (changed) { await this.load(true); this.toast("Updated with the latest NetSuite data"); }
      else this.render();
    } catch { /* offline: keep showing what we have */ }
    this.schedulePoll();
  }

  toast(msg: string) {
    let t = document.querySelector<HTMLDivElement>(".toast");
    if (!t) { t = document.createElement("div"); t.className = "toast glass"; document.body.appendChild(t); }
    t.textContent = msg;
    t.classList.add("show");
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => t!.classList.remove("show"), 2600);
  }

  /* ---------------- render ---------------- */

  render() {
    hideTip();
    const view = this.views[this.page];
    const content = this.error
      ? html`<div class="banner err glass">${icons.alert}<div><b>Couldn't load this page.</b> ${this.error}</div><span class="spacer"></span><button class="btn" @click=${() => void this.load()}>Try again</button></div>`
      : this.data === null
        ? html`<div class="grid g-kpi">${[1, 2, 3, 4].map(() => html`<div class="glass kpi skeleton loading-card"></div>`)}</div><div class="glass card skeleton mt" style="height:320px"></div>`
        : view.render(this, this.data, this.param);
    render(html`
      <div class="backdrop"></div>
      <div class="shell">
        ${this.sidebar()}
        <main class="main">
          ${this.topbar(view)}
          ${this.status.demo ? html`<div class="banner demo">${icons.alert}<div><b>Demo data.</b> Real reps and divisions, made-up customers and amounts. It disappears the moment the first NetSuite export is imported.</div></div>` : nothing}
          <div class="fade-in">${content}</div>
        </main>
      </div>`, this.root);
  }

  private sidebar(): TemplateResult {
    const toggleTheme = () => {
      const next = document.documentElement.dataset.theme === "light" ? "dark" : "light";
      document.documentElement.dataset.theme = next;
      try { localStorage.setItem("ffd-theme", next); } catch { /* private mode */ }
      this.render();
      this.reload(); // charts pick up new colors
    };
    const light = document.documentElement.dataset.theme === "light";
    return html`<aside class="sidebar glass">
      <div class="brand"><div class="brand-mark">${icons.flame}</div><div><div class="brand-name">Feld Fire</div><div class="brand-sub">Sales Dashboard</div></div></div>
      <nav class="nav">
        ${[...NAV, ...(this.me.user.isAdmin ? [{ page: "users" as Page, label: "Users", icon: icons.key }] : [])].map((n, i) => html`${i === 5 || i === 7 ? html`<div class="nav-sep"></div>` : nothing}
          <a href=${this.href(n.page)} aria-current=${this.page === n.page ? "page" : "false"} title=${n.label}>${n.icon}<span>${n.label}</span></a>`)}
      </nav>
      <div class="sidebar-foot">
        <a class="who plain" href="#/account" title="Your account and password"><div class="avatar">${initials(this.me.user.name)}</div><div class="who-text"><div class="who-name">${this.me.user.name}</div><div class="who-scope">${this.me.scope.label} · Account</div></div></a>
        <div class="row">
          <button class="btn ghost" style="flex:1" @click=${toggleTheme} title="Switch theme">${light ? icons.moon : icons.sun}<span>${light ? "Dark" : "Light"}</span></button>
          <button class="btn ghost icon" title="Sign out" aria-label="Sign out" @click=${async () => { await api.logout().catch(() => undefined); location.reload(); }}>${icons.logout}</button>
        </div>
      </div>
    </aside>`;
  }

  private topbar(view: View): TemplateResult {
    const f = this.filters;
    const curYear = Number(this.status.asOf.slice(0, 4));
    const firstYear = this.status.firstDate ? Math.max(2018, Number(this.status.firstDate.slice(0, 4))) : curYear;
    const years: number[] = [];
    for (let y = curYear; y >= firstYear && years.length < 4; y--) years.push(y);
    const reps = this.me.reps.filter((r) => f.manager === null || r.managerId === f.manager || r.id === f.manager);
    const fresh = this.status.updatedAt ? Date.now() - Date.parse(this.status.updatedAt) < 36 * 3600 * 1000 : false;
    const show = (k: (typeof view.filters)[number]) => view.filters.includes(k);
    return html`<header class="topbar glass">
      <div class="page-title">${view.title(this)}<small>through ${fmtDate(this.status.asOf, { month: "short", day: "numeric" })}</small></div>
      <div class="filters">
        ${show("year") && years.length > 1 ? html`<div class="seg" role="group" aria-label="Year">${years.map((y) => html`<button aria-pressed=${f.year === y} @click=${() => this.setFilter({ year: y })}>${y}</button>`)}</div>` : nothing}
        ${show("manager") && this.me.managers.length ? html`<select class="select" aria-label="Manager" @change=${(e: Event) => { const v = (e.target as HTMLSelectElement).value; this.setFilter({ manager: v ? Number(v) : null }); }}>
          <option value="" ?selected=${f.manager === null}>All managers</option>
          ${this.me.managers.map((m) => html`<option value=${m.id} ?selected=${f.manager === m.id}>${m.name}</option>`)}
        </select>` : nothing}
        ${show("rep") ? html`<select class="select" aria-label="Rep" @change=${(e: Event) => { const v = (e.target as HTMLSelectElement).value; this.setFilter({ rep: v ? Number(v) : null }); }}>
          <option value="" ?selected=${f.rep === null}>All reps</option>
          ${reps.map((r) => html`<option value=${r.id} ?selected=${f.rep === r.id}>${r.name}</option>`)}
        </select>` : nothing}
        ${show("division") ? html`<select class="select" aria-label="Division" @change=${(e: Event) => { const v = (e.target as HTMLSelectElement).value; this.setFilter({ division: v || null }); }}>
          <option value="" ?selected=${f.division === null}>All divisions</option>
          ${this.me.divisions.map((d) => html`<option value=${d.key} ?selected=${f.division === d.key}>${d.name}</option>`)}
        </select>` : nothing}
        <span class="live ${fresh ? "" : "stale"}" title=${`${this.status.updatedAt ? `Last NetSuite pull ${new Date(this.status.updatedAt).toLocaleString()}. ` : "No NetSuite pulls yet. "}Pulls every 15 minutes 7am–6pm Mon–Fri, plus a full check every night. This page refreshes itself.`}><i></i>${this.loading ? "Loading…" : `Updated ${timeAgo(this.status.updatedAt)}`}</span>
      </div>
    </header>`;
  }
}
